import React, { useState, useRef } from 'react';
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import {
  ArrowRight,
  Play,
  Menu,
  X,
  ChevronDown,
  ChevronUp,
  ChevronLeft, 
  ChevronRight,
  Star,
  MousePointerClick,
  MonitorPlay,
  BarChart3
} from 'lucide-react';

// --- Reusable Placeholder Component ---
// This indicates where you should insert your images
const ImagePlaceholder = ({ width, height, label, className = "" }) => (
  <div
    className={`bg-gray-200 border-2 border-dashed border-gray-400 flex items-center justify-center text-gray-500 text-sm font-medium text-center p-2 ${className}`}
    style={{ width: width || '100%', height: height || '100%' }}
  >
    {label || "Insert Image Here"}
  </div>
);

// --- Sub-Components ---

const Navbar = () => {

  return (
    <nav className="flex justify-between items-center py-6 px-6 xl:px-16 2xl:px-28 relative z-10">
      <Link to='/' aria-label='qlite logo'>
        <img tabIndex={0} className='cursor-pointer w-16 md:w-20 mx-auto lg:ml-0 brightness-0' src='../images/qlite-logo.webp' alt='qlite logo' />
      </Link>

      {/* Desktop Nav */}
      <div className="flex items-center gap-3">
        <Link to="/login" className="text-[#2B2B2B] px-4 md:px-8 py-3 text-sm inter-600 border md:border-[#2B2B2B] border-white rounded-full hover:text-white md:bg-transparent bg-white hover:bg-primary hover:border-primary transition-all ease-in-out duration-500">
          Login
        </Link>
        <Link to="/getquote"
          className="text-black px-4 md:px-8 py-3 text-sm rounded-full inter-600 bg-gradient-to-b from-[#F2CE64] to-[#EEB50A] 
            hover:from-[#F2CE64]/90 hover:to-[#EEB50A]/90 border border-transparent transition-all ease-in-out duration-500"
          // style={{ background: 'linear-gradient(180deg, #F2CE64 0%, #EEB50A 66.3%)' }}
        >
          Request Demo
        </Link>
      </div>
    </nav>
  );
};

const Hero = () => {
  return (
    <div className="relative pt-10 pb-40 md:pb-32 px-6 md:px-6 md:max-w-6xl 2xl:max-w-7xl mx-auto">
      <div className="relative">
        <img src="/images/home/qlite-text-group.webp" alt="Students" className="w-full h-full object-cover mb-4 md:hidden rounded-full" />
        <h1 className="text-[2.25rem] md:text-[5.25rem] 2xl:text-[6rem] inter-600 text-[#232323] leading-normal md:leading-[7.87rem] 2xl:leading-[8.95rem] md:mb-8">
          Building Quantum <span className="md:flex items-center md:mt-4">
            <span className="md:inline-block hidden mr-8 align-middle h-[6.87rem] w-[29rem] rounded-full overflow-hidden relative top-1">
              <img src="/images/home/qlite-text-group.webp" alt="Students" className="w-full h-full object-cover" />
            </span>
             Literacy for </span> tomorrow’s world.
        </h1>
        <div className="md:absolute -right-4 top-0 pt-4 md:pt-8 md:pb-5 md:w-[22rem] h-full flex flex-col justify-between">
          <p className="text-[#272727]/90 md:mb-0 text-base inter-400 leading-[22px]">
            Explore the quantum universe through stories, visual lessons, and interactive challenges that turn curiosity into understanding.
          </p>

          <div className="md:flex mt-24 md:mt-0">
            <Link to="/getquote" className="group flex items-center justify-center gap-2 bg-[#47D28E] hover:bg-primary border-[6px] border-[#50CB8F]/22 text-[#003119] hover:text-white px-6 md:px-14 py-5 rounded-full inter-500 text-[1.375rem] leading-[1.72] transition-all ease-in-out duration-500">
              Request a Demo
              <svg className="group-hover:translate-x-1 group-hover:scale-110 transition-all ease-in-out duration-500" xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
                <path className="group-hover:fill-white transition-all ease-in-out duration-500" d="M4.41947 -3.41535e-05L4.41947 2.47484L14.319 2.47484L5.04404e-05 16.7938L1.76782 18.5615L16.0867 4.24261V14.1421H18.5616V-3.41535e-05H4.41947Z" fill="#003119"/>
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const Features = () => {
  const topics = [
    "Sensors", "Classical and Quantum Theory",
    "Cryogenics", "Computing and Quantum Information Science",
    "Photonics", "Networks and Communication",
    "Machine Learning", "Lasers"
  ];

  const [play, setPlay] = useState(false);

  return (
<div className="bg-white rounded-t-[3rem] md:-mt-10 -mt-20 pt-14 md:pt-24 pb-14 md:pb-[6.5rem] px-6 xl:px-16 2xl:px-28 relative z-10 shadow-[0_-20px_40px_rgba(0,0,0,0.05)]">      
  <div className="">
        <div className="text-center flex items-center flex-col mb-14 md:mb-24">
          <h2 className="text-2xl md:text-[3.25rem] inter-700 bg-[linear-gradient(91deg,#CC8619_20%,#EA6343_75%)] bg-clip-text text-transparent mb-8 md:mb-6 tracking-wide leading-tight">
            Designed to Simplify <br/>Complex Quantum Concepts
          </h2>
          <p className="text-[#494949] text-base inter-400 md:max-w-2xl mx-auto">
            QliteX is an e-learning platform that turns quantum concepts into short narrative lessons, animations and hands-on activities.
          </p>
        </div>

        <div className="flex flex-wrap gap-4 md:gap-5">
          {/* Left: Topics Grid */}
          <div className="w-full md:w-[45%] bg-[#FFF3D7] gap-1.5 md:gap-2 p-1.5 md:p-2.5 rounded-2xl relative flex flex-col overflow-hidden">
            <div className="flex gap-2 md:gap-2.5">
              <div className="w-[36%] bg-[#F8E8C0] rounded-md p-5 h-14 md:h-18 flex items-center justify-center">
                <p className="text-xs md:text-base inter-500 text-center tracking-[-1px] text-[#523901]">Sensors</p>
              </div>
              <div className="w-[64%] bg-[#F8E8C0] rounded-md p-5 h-14 md:h-18 flex items-center justify-center">
                <p className="text-xs md:text-base inter-500 text-center tracking-[-1px] text-[#523901]">Classical and Quantum Theory</p>
              </div>
            </div>
            <div className="flex gap-2 md:gap-2.5">
              <div className="w-[30%] bg-[#F8E8C0] rounded-md p-5 h-14 md:h-18 flex items-center justify-center">
                <p className="text-xs md:text-base inter-500 text-center tracking-[-1px] text-[#523901]">Cryogenics</p>
              </div>
              <div className="w-[70%] bg-[#F8E8C0] rounded-md p-5 h-14 md:h-18 flex items-center justify-center">
                <p className="text-xs md:text-base inter-500 text-center tracking-[-1px] text-[#523901]">Computing and Quantum Information Science</p>
              </div>
            </div>
            <div className="flex justify-center py-4">
              <p className="inter-700 text-2xl md:text-3xl text-[#0E3581] tracking-[-0.96px]">With 8 Topics & AI</p>
            </div>
            <div className="flex gap-2 md:gap-2.5">
              <div className="w-[37%] bg-[#F8E8C0] rounded-md p-5 h-14 md:h-18 flex items-center justify-center">
                <p className="text-xs md:text-base inter-500 text-center tracking-[-1px] text-[#523901]">Photonics</p>
              </div>
              <div className="w-[63%] bg-[#F8E8C0] rounded-md p-5 h-14 md:h-18 flex items-center justify-center">
                <p className="text-xs md:text-base inter-500 text-center tracking-[-1px] text-[#523901]">Networks and Communication</p>
              </div>
            </div>
            <div className="flex gap-2 md:gap-2.5">
              <div className="w-[63%] bg-[#F8E8C0] rounded-md p-5 h-14 md:h-18 flex items-center justify-center">
                <p className="text-xs md:text-base inter-500 text-center tracking-[-1px] text-[#523901]">Machine Learning</p>
              </div>
              <div className="w-[37%] bg-[#F8E8C0] rounded-md p-5 h-14 md:h-18 flex items-center justify-center">
                <p className="text-xs md:text-base inter-500 text-center tracking-[-1px] text-[#523901]">Lasers</p>
              </div>
            </div>
          </div>

          {/* Right: Quantum Image/Video */}
          <div className="w-auto flex-1 relative rounded-2xl overflow-hidden bg-[#1A1A1A]/10 group cursor-pointer md:max-h-[21.5rem]">
          {!play && (
            <div onClick={() => setPlay(true)}>
              <img
                src="/images/home/ai-video-thumb.webp"
                alt="Quantum Device"
                className="w-full h-full object-cover"
              />

              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className="bg-white/90 w-20 h-20 rounded-full flex items-center justify-center shadow-2xl group-hover:scale-110 transition"
                >
                  <Play className="w-8 h-8 fill-black text-black ml-1" />
                </div>
              </div>
            </div>
          )}

          {/* When playing show video */}
          {play && (
            <video
              src="https://d2rig861j9jdm5.cloudfront.net/production/marketing/QLiteMarketing_02-V8%20(%20Logo%20Updated%20).mp4"
              autoPlay
              controls
              className="w-full h-full object-cover"
            />
          )}
          </div>
        </div>

        {/* Partners Logo Strip */}
        <div className="mt-14 md:mt-[5.5rem] flex flex-col md:flex-row items-center justify-between border-t border-[#DFDFDF] pt-4 md:pt-11">
          <p className="text-[#3B3B3B] inter-500 text-xl mb-6 md:mb-0 md:max-w-xs">Built for Industry, Government, and Academics</p>
          <div className="flex justify-between md:justify-initial md:gap-20 transition w-full md:w-auto">
            {/* Replace these divs with partner logos */}
            <div className="h-13 md:h-14.5 w-auto"><img src="/images/home/nqln-logo.webp" alt="Partner 1" className="w-full h-full object-contain" /></div>
            <div className="h-13 md:h-14.5 w-auto"><img src="/images/home/science-logo.webp" alt="Partner 2" className="w-full h-full object-contain" /></div>
          </div>
        </div>
      </div>
    </div>
  );
};

const HowItWorks = () => {
  const steps = [
    {
      icon: "/images/home/work-one.svg",
      title: "Choose a Technology",
      desc: "Pick Grade-Appropriate Lessons Like Photonics And Lasers."
    },
    {
      icon: "/images/home/work-two.svg",
      title: "Learn & Play",
      desc: "Videos, Interactive Simulations And Gamified Activities."
    },
    {
      icon: "/images/home/work-three.svg",
      title: "Test & Track",
      desc: "Pre/Post Assessments, Scores, And Downloadable Certificates."
    }
  ];

  return (
    <div className="bg-[#FFF8EF] pt-14 md:pt-24 pb-20 md:pb-[10.935rem] px-6 xl:px-16 2xl:px-28 relative overflow-hidden">
      {/* Background decoration (wavy line) */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        <svg xmlns="http://www.w3.org/2000/svg" width="1440" height="686" viewBox="0 0 1440 686" fill="none">
          <path d="M-66 564.672C128.561 467.524 599.693 80.26 674.198 125.622C784.675 192.886 524.897 660.276 690.86 647.753C823.63 637.735 1298.81 336.598 1579 -41" stroke="#F38F0E" stroke-opacity="0.08" stroke-width="75"/>
        </svg>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex flex-col md:flex-row justify-between md:items-end mb-14 md:mb-24">
          <h2 className="text-2xl md:text-[3.25rem] inter-700 bg-[linear-gradient(91deg,#CC8619_20%,#EA6343_75%)] bg-clip-text text-transparent tracking-wide leading-tight">How It Works</h2>
          <p className="text-[#262626] md:max-w-xs text-base mt-5 md:mt-0">
            Every learner's journey in QliteX is guided, measurable, and fun — turning curiosity into quantum confidence.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 md:gap-10">
          {steps.map((step, idx) => (
            <div key={idx} className="bg-white p-6 md:p-11 rounded-2xl border border-[#F58728] transition">
              <div className="bg-[#F58728]/10 w-[46px] h-[46px] rounded-md flex items-center justify-center mb-8 md:mb-9">
                <img alt="" src={step.icon} />
              </div>
              <h3 className="text-2xl md:text-[1.75rem] inter-600 text-[#1F2937] tracking-[-1.12px] mb-4 md:mb-8">{step.title}</h3>
              <p className="text-[#6B7280] text-sm md:text-base inter-400">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const slidesData = [
  {
    name: "Joe Reddix",
    rating: 5,
    ratingCount: "5.0",
    image: "/images/home/joe.webp",
    designation: "President & CEO",
    company: "The Reddix Group",
    quote:
      "QLITEX is a great tool for K-12 Quantum Literacy Knowledge containing examples and easy to follow audio and visual tools, in a well-defined format for learning and retention.",
  },
  {
    name: "Lily Milliner",
    rating: 5,
    ratingCount: "5.0",
    image: "/images/home/lily.webp",
    designation: "CEO, Build IT Up & Author",
    company: "Quantum Devotions: The Physics of Faith",
    quote: "A stunning leap forward. The design QLITEX platform creates a learning environment where complexity becomes clarity, and every user feels invited into the quantum future.",
  },
  {
    name: "Shane Perkins",
    rating: 5,
    ratingCount: "5.0",
    image: "/images/home/shane.webp",
    designation: "Unite Digital Holdings",
    company: "Volunteer NQLN",
    quote:
      "I loved the educational approach of bite-sized learning modules. It helped me compartmentalize and learn quantum topics in a way I can remember.",
  },
  {
    name: "Dr. Tim Akers",
    rating: 5,
    ratingCount: "5.0",
    image: "/images/home/tim.webp",
    designation: "Chief Research Officer",
    company: "California State University, San Bernardino",
    quote:
      "QLITEX is exciting and truly supports quantum literacy for neurodivergent learners. It offers games, QBot, AI tools, micro-learning, quizzes, assessments, cohort comparisons, animations, and many engaging ways for students—from grades 8–12 to community and vocational colleges—to learn quantum topics.",
  },
];

const TestimonialCard = ({ name, role, quote, rating, image, designation, company, ratingCount }) => (
  <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-100 flex flex-col justify-between h-full min-h-[406px]">
    <div>
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-full overflow-hidden shrink-0">
          <img src={image} alt={name} className="w-full h-full object-cover" />
        </div>
        <div>
          <h4 className="inter-700 text-xl text-[#242424]">{name}</h4>
          <p className="inter-400 text-sm text-[#242424]">{designation}</p>
          <p className="inter-400 text-sm text-[#242424]">{company}</p>
        </div>
      </div>

      <p className="inter-400 text-base text-[#3B3B3B] mb-8">"{quote}"</p>
    </div>
    <div className="flex items-center gap-2">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${i < rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`}
        />
      ))} <span className="text-[#525252] text-sm inter-600">{ratingCount}</span>
    </div>
  </div>
);

const Testimonials = () => {
  const swiperRef = useRef(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  const slideCount = slidesData.length;
  // Ensure slidesPerView is strictly less than slideCount to avoid lock.
  const desktopSlides = slideCount > 3 ? 3 : Math.max(1, slideCount - 1);
  const tabletSlides = slideCount > 2 ? 2 : Math.max(1, slideCount - 1);

  return (
    <div className="bg-white py-16 md:py-20 px-4 xl:px-16 2xl:px-28">
      <div className="text-center flex items-center flex-col mb-7">
        <h2 className="text-2xl md:text-[3.25rem] inter-700 bg-[linear-gradient(91deg,#CC8619_20%,#EA6343_75%)] bg-clip-text text-transparent tracking-wide leading-tight">
          Learner Stories & Successes
        </h2>
      </div>

      <div className="relative">
        <div className="flex items-center gap-2 absolute right-4 -bottom-3">
          {/* Prev Button (we attach ref, Swiper will use it) */}
          <button
            ref={prevRef}
            className="group bg-white hover:bg-[#424242] border border-[#8A8A8A] hover:border-[#424242] p-2 rounded-full hover:shadow transition-all ease-in-out duration-500 z-10"
            aria-label="Previous"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <g clipPath="url(#clip0_50_565)">
                <path className="group-hover:fill-white transition-all ease-in-out duration-500" d="M0.183312 7.55759C0.1835 7.5574 0.183657 7.55719 0.183875 7.557L3.44963 4.307C3.69428 4.06353 4.09 4.06444 4.33353 4.30912C4.57703 4.55378 4.57609 4.9495 4.33144 5.193L2.13881 7.375H15.375C15.7202 7.375 16 7.65481 16 8C16 8.34519 15.7202 8.625 15.375 8.625H2.13884L4.33141 10.807C4.57606 11.0505 4.577 11.4462 4.3335 11.6909C4.08997 11.9356 3.69422 11.9364 3.44959 11.693L0.183844 8.443C0.183656 8.44281 0.183499 8.44259 0.183281 8.4424C-0.0615005 8.19809 -0.0607185 7.80109 0.183312 7.55759Z" fill="#8A8A8A"/>
              </g>
              <defs>
                <clipPath id="clip0_50_565">
                  <rect width="16" height="16" fill="white" transform="matrix(-1 0 0 1 16 0)"/>
                </clipPath>
              </defs>
            </svg>
          </button>

          {/* Next Button (we attach ref, Swiper will use it) */}
          <button
            ref={nextRef}
            className="group bg-white hover:bg-[#424242] border border-[#8A8A8A] hover:border-[#424242] p-2 rounded-full hover:shadow transition-all ease-in-out duration-500 z-10"
            aria-label="Next"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <g clipPath="url(#clip0_50_559)">
                <path className="group-hover:fill-white transition-all ease-in-out duration-500" d="M15.8167 7.55759C15.8165 7.5574 15.8163 7.55719 15.8161 7.557L12.5504 4.307C12.3057 4.06353 11.91 4.06444 11.6665 4.30912C11.423 4.55378 11.4239 4.9495 11.6686 5.193L13.8612 7.375H0.625C0.279813 7.375 0 7.65481 0 8C0 8.34519 0.279813 8.625 0.625 8.625H13.8612L11.6686 10.807C11.4239 11.0505 11.423 11.4462 11.6665 11.6909C11.91 11.9356 12.3058 11.9364 12.5504 11.693L15.8162 8.443C15.8163 8.44281 15.8165 8.44259 15.8167 8.4424C16.0615 8.19809 16.0607 7.80109 15.8167 7.55759Z" fill="#8A8A8A"/>
              </g>
              <defs>
                <clipPath id="clip0_50_559">
                  <rect width="16" height="16" fill="white"/>
                </clipPath>
              </defs>
            </svg>
          </button>
        </div>

        <Swiper
          modules={[Navigation, Pagination]}
          loop={true}
          spaceBetween={24}
          speed={600}
          effect="slide"
          // connect navigation elements via onBeforeInit to avoid timing/ref issues
          onBeforeInit={(swiper) => {
            // assign actual DOM nodes
            if (typeof swiper.params.navigation !== "boolean") {
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
            }
          }}
          onSwiper={(swiper) => {
            // init navigation after we set the params
            // this ensures prev/next DOM nodes are registered
            swiperRef.current = swiper;
            setTimeout(() => {
              if (swiper.navigation && typeof swiper.navigation.init === "function") {
                swiper.navigation.init();
                swiper.navigation.update();
              }
            }, 0);
          }}
          pagination={{
            el: ".custom-pagination",
            clickable: true,
            bulletClass: "swiper-custom-bullet",
            bulletActiveClass: "swiper-custom-bullet-active",
          }}
          // adaptive breakpoints so slidesPerView < total slides (prevents pagination lock)
          breakpoints={{
            0: { slidesPerView: 1 },
            768: { slidesPerView: tabletSlides },
            1024: { slidesPerView: desktopSlides },
          }}
          // extra safety: make sure swiper is not allowed to lock everything
          watchOverflow={false}
          className="!py-8 !px-4"
        >
          {slidesData.map((s, idx) => (
            <SwiperSlide key={idx}>
              <TestimonialCard {...s} />
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Custom Pagination */}
        <div className="custom-pagination flex justify-center gap-6 md:mt-16"></div>
      </div>

      {/* Custom Pagination CSS + override if swiper adds .swiper-pagination-lock */}
      <style>{`
        .swiper-custom-bullet {
          width: 8px;
          height: 8px;
          background: #BFBFBF;
          border-radius: 9999px;
          display: inline-block;
          cursor: pointer;
          transition: all 0.2s;
        }
        .swiper-custom-bullet-active {
          background: #2B2C2C;
        }
        /* If Swiper marks pagination as locked, force it visible and clickable */
        .custom-pagination.swiper-pagination-lock {
          opacity: 1 !important;
          pointer-events: auto !important;
        }
      `}</style>
    </div>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    { question: "Who is QliteX for?", answer: "QliteX is designed for middle and high school students (grades 6–12), teachers, school administrators, and community colleges seeking an accessible and engaging introduction to quantum concepts." },
    { question: "How are the lessons aligned to standards?", answer: "Our lessons map directly to NGSS and typical state physics curriculums." },
    { question: "What types of content and activities are included in a module?", answer: "Each module contains video lectures, interactive WebGL simulations, and quizzes." },
    { question: "Do students earn certificates or badges?", answer: "Yes, completion certificates are awarded upon passing final assessments." },
    { question: "How do the pre/post assessments work?", answer: "These are diagnostic tools to measure knowledge retention before and after modules." },
  ];

  return (
    <div className="bg-white py-10 px-4 xl:px-16 2xl:px-28">
      <div className="flex flex-wrap md:gap-9">
        <div className="md:w-[40%] flex items-center flex-col mb-8 md:mb-0">
          <h2 className="text-2xl md:text-[3.25rem] inter-700 bg-[linear-gradient(91deg,#CC8619_20%,#EA6343_75%)] bg-clip-text text-transparent tracking-wide leading-tight">
            Frequently Asked<br />Questions
          </h2>
        </div>

        <div className="md:w-auto flex-1 flex flex-col gap-5 md:gap-6">
          {faqs.map((faq, idx) => {
            const isOpen = openIndex === idx;
            return (
              <div
                key={idx}
                className={`border border-[#909291]/24 rounded-xl p-6 transition-all duration-300 ${
                  isOpen ? "bg-[#FAFAFA]" : "bg-white"
                }`}
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? -1 : idx)}
                  className="flex justify-between items-center w-full text-left text-base inter-500 text-[#0E120F] hover:text-orange-500 transition-all ease-in-out duration-300"
                >
                  <span className="flex-auto">
                    {faq.question}
                  </span>
                  <span className="w-5">
                    {isOpen ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </span>
                </button>

                {/* Animated Answer */}
                <div
                  className={`overflow-hidden transition-all duration-500 ease-out ${
                    isOpen ? "max-h-40 opacity-100 mt-4" : "max-h-0 opacity-0 mt-0"
                  }`}
                >
                  <div className="text-sm text-[#3E3E3E] inter-400 leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const FooterCTA = () => {
  return (
    <div className="bg-white mt-10 pb-14 px-6 xl:px-9 2xl:px-18">
      <div className="rounded-[20px] overflow-hidden relative">
        <div className="flex flex-col md:flex-row items-center gap-12 pt-14 md:pt-16"
          style={{ background: 'conic-gradient(from 180deg at 52.29% 50%, #447FFF 0deg, #B442AF 60.00000178813934deg, #E4456B 120.00000357627869deg, #E38025 180deg, #E4456B 240.00000715255737deg, #E4456B 299.9999928474426deg, #203BC7 360deg)' }}
        >

          <div className="md:w-[45%] text-white px-4 md:pl-12 md:pr-0">
            <h2 className="text-3xl md:text-5xl leading-tight tracking-[-1.92px] inter-700 mb-4 md:mb-6">Ready to explore & Start your Quantum journey</h2>
            <p className="text-black text-base inter-400 mb-10 md:mb-14 max-w-lg">Book a demo to review student outcomes, teacher workflows, and implementation options that scale across classrooms.</p>
            <div className="flex">
              <Link to="/getquote"
                className="w-full md:w-auto text-center text-black px-8 py-4 text-base bg-[#EFB731] hover:bg-white rounded-full inter-600 transition-all ease-in-out duration-500"
              >
                Request a Demo
              </Link>
            </div>
          </div>

          <div className="flex-1 w-auto relative pl-4 md:pl-0">
            {/* Dashboard Mockup Image Placeholder */}
            <div className="rounded-[20px] w-full">
              <img src="/images/home/explore-dashboard.webp" alt="Dashboard" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Footer = () => (
  <footer className="bg-white pb-8 px-6 xl:px-16 2xl:px-28">
    <div className="border-t-2 border-[#F58728] pt-8 md:pt-11 flex flex-col md:flex-row justify-between md:items-end gap-8 md:gap-0">
      <div className="">
        <Link to='/' aria-label='qlite logo'>
          <img tabIndex={0} className='cursor-pointer w-[6.625rem] md:w-20 md:mx-auto lg:ml-0' src='../images/ina-logo-color.svg' alt='ina logo' />
        </Link>
        <p className="text-base text-black mt-4">Powdered by Ina Solutions Inc.</p>
      </div>
      <div className="flex flex-col md:items-end gap-4">
        <Link to='/' aria-label='qlite logo'>
          <img tabIndex={0} className='cursor-pointer w-[6.625rem] md:w-20 md:mx-auto lg:ml-0 brightness-0' src='../images/qlite-logo.webp' alt='qlite logo' />
        </Link>
        <div className="flex flex-wrap items-center gap-3 md:gap-0">
          <a href="mailto:sales@quantumliteracy.org" className="group text-sm inter-500 text-black hover:text-primary flex items-center gap-2 md:border-r border-black pr-4 transition-all ease-in-out duration-500">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path className="group-hover:fill-primary transition-all ease-in-out duration-500" d="M12.9592 10.8301L11.2842 12.5108C10.6049 13.1924 9.40979 13.2071 8.71584 12.5108L7.04073 10.8301L1.02448 16.8659C1.24842 16.9694 1.49533 17.0312 1.75783 17.0312H18.2422C18.5047 17.0312 18.7515 16.9695 18.9754 16.8659L12.9592 10.8301Z" fill="black"/>
              <path className="group-hover:fill-primary transition-all ease-in-out duration-500" d="M18.2422 2.96875H1.75782C1.49532 2.96875 1.2484 3.03055 1.02454 3.1341L7.45333 9.58422C7.45375 9.58465 7.45426 9.58473 7.45469 9.58516C7.45512 9.58559 7.4552 9.58617 7.4552 9.58617L9.54555 11.6834C9.76758 11.9055 10.2325 11.9055 10.4545 11.6834L12.5445 9.58652C12.5445 9.58652 12.545 9.58559 12.5454 9.58516C12.5454 9.58516 12.5463 9.58465 12.5468 9.58422L18.9754 3.13406C18.7515 3.03047 18.5047 2.96875 18.2422 2.96875Z" fill="black"/>
              <path className="group-hover:fill-primary transition-all ease-in-out duration-500" d="M0.186953 3.95361C0.0710937 4.18791 0 4.44799 0 4.72658V15.2735C0 15.5521 0.0710156 15.8121 0.186914 16.0464L6.21359 10.0002L0.186953 3.95361Z" fill="black"/>
              <path className="group-hover:fill-primary transition-all ease-in-out duration-500" d="M19.813 3.95361L13.7864 10.0003L19.813 16.0466C19.9289 15.8123 20 15.5522 20 15.2735V4.72666C20 4.44799 19.9289 4.18791 19.813 3.95361Z" fill="black"/>
            </svg>
            sales@quantumliteracy.org
          </a>
          <span className="md:px-4 md:border-r border-black">© Copyright National Quantum Literacy Network, USA</span>
          <a href="/privacy-policy" className="md:pl-4 text-sm text-[#3A3A3A] hover:text-primary inter-500 transition-all ease-in-out duration-500">Privacy Policy</a>
        </div>
      </div>
    </div>
  </footer>
);

export default function Home() {
  return (
    <div className="min-h-screen font-sans text-gray-900 relative">
      {/* Top Gradient Background */}
      <div className="absolute top-0 left-0 w-full h-[15%] md:h-[900px] -z-10 md:rounded-b-[5rem] overflow-hidden">
        <img
          src="/images/home/qlite-banner.webp"
          alt="Hero Background"
          className="w-full h-full object-cover md:block hidden"
        />
        <img
          src="/images/home/qlite-banner-mob.webp"
          alt="Hero Background"
          className="w-full h-full object-cover md:hidden block"
        />
      </div>

      <Navbar />
      <Hero />
      <Features />
      <HowItWorks />
      <Testimonials />
      <FAQ />
      <FooterCTA />
      <Footer />
    </div>
  );
}